import type {MaybeRef, Ref} from 'vue';
import {ref, watch} from 'vue';
import {projectListRouteFiltersSchema} from '~/features/projects-list/utils';

/** POC for a custom composable for using cache, as pinia-persisted-state is buggy */
export function useCachedValue<T>(target: string, defaultValue: MaybeRef<T>, options?: {strategy?: 'local' | 'session'}) {
    const {strategy = 'session'} = options ?? {};
    const cachedRef = ref(defaultValue) as Ref<T>;
    const storage = strategy === 'local' ? localStorage : sessionStorage;

    function deserializeItem(): T | string | undefined {
        if (import.meta.client) {
            const item = storage?.getItem(target);

            if (item) {
                try {
                    const parsed = JSON.parse(item);
                    return parsed as T;
                } catch (e) {
                    return undefined;
                }
            }
        }
    }

    function read() {
        const storedValue = deserializeItem();
        if (storedValue && typeof storedValue !== 'string') {
            const coerceValues = projectListRouteFiltersSchema.parse(storedValue);
            cachedRef.value = {
                ...cachedRef.value,
                ...coerceValues,
            };
        }
    }

    function udpateStore(newValue?: T) {
        const serializedValue = JSON.stringify(newValue);
        storage.setItem(target, serializedValue);
    }

    watch(cachedRef, udpateStore, {deep: true});

    return {cachedRef, read};
}
